<template>
  <div>
    <div class="debrief-nav">
      <i
        @click="closeDebrief"
        class="material-icons m-2 cursor-pointer user-select-none"
      >
        close
      </i>
    </div>
    <div class="debrief-mode-selector btn-group d-flex w-25 m-auto">
      <div
        @click="selectedPage = 'overview'"
        class="btn cursor-pointer"
        :class="selectedPage === 'overview' ? 'btn-primary' : 'unselected-btn'"
      >
        Overview
      </div>
      <div
        @click="selectedPage = 'events'"
        class="btn cursor-pointer"
        :class="selectedPage === 'events' ? 'btn-primary' : 'unselected-btn'"
      >
        Event Log
      </div>
    </div>

    <div v-if="selectedPage === 'overview'" class="overview">
      <div class="d-flex">
        <div
          class="debrief-content-left d-flex flex-column justify-content-between w-50 p-3"
        >
          <div v-for="(value, name) in leftMenuItems" class="d-flex my-1">
            <div class="w-50">{{ name }}</div>
            <div class="w-50 text-break">{{ value }}</div>
          </div>
        </div>
        <div
          class="debrief-content-right d-flex flex-column justify-content-between w-50 p-3"
        >
          <div v-for="(value, name) in rightMenuItems" class="d-flex my-1">
            <div class="w-50">{{ name }}</div>
            <div class="w-50 text-break">{{ value }}</div>
          </div>
        </div>
      </div>

      <div
        class="debrief-scores m-2 d-flex justify-content-around flex-wrap text-center"
      >
        <div
          v-for="(score, name) in debriefScores"
          class="debrief-score m-1 px-4 py-2"
        >
          <div class="score-name">{{ name }}</div>
          <div class="score-num">{{ score }}</div>
        </div>
      </div>
    </div>

    <div v-else-if="selectedPage === 'events'" class="event-log">
      <div v-for="event in events" class="d-flex border-bottom m-2">
        <img
          width="50"
          :alt="event.name"
          class="image-filter m-2"
          :src="event.img"
        />
        <div class="d-flex align-items-center">
          <div class="mx-2">{{ event.time }}</div>
          <div class="d-flex">
            {{ event.name }}
            <i class="material-icons user-select-none">{{ event.checkIcon }}</i>
          </div>
        </div>
      </div>
    </div>

    <div class="debrief-footer"></div>
  </div>
</template>

<script>
import {
  buildDebriefEvent,
  getFirstAndLastNames,
  getImageForDebriefEvent,
} from "@/utils/tms.helper";
import {
  getDateFromUtcString,
  getFormattedDateStringFromDateObject,
  msToFullTime,
} from "@/utils/datetime.helper";

export default {
  name: "TmsDebrief",
  props: ["debrief"],
  data() {
    return {
      selectedPage: "overview", // overview | events
      studentFirstNames: "",
      studentLastName: "",
    };
  },
  mounted() {
    const studentNames = getFirstAndLastNames(this.debrief.student.name);

    this.studentFirstNames = studentNames[0];
    this.studentLastName = studentNames[studentNames.length - 1];
  },
  computed: {
    leftMenuItems() {
      if (!this.debrief.scenario) return [];

      return {
        Surname: this.studentLastName,
        "First Name": this.studentFirstNames,
        "Student #": this.debrief.student.studentId,
        Email: this.debrief.student.email,
        "Scenarios on record": this.numDebriefsForStudent,
      };
    },
    rightMenuItems() {
      if (!this.debrief.scenario) return [];

      const startDate = getDateFromUtcString(this.debrief.startDate);
      const endDate = getDateFromUtcString(this.debrief.endDate);
      const timeDiff = endDate - startDate;

      return {
        "Scenario Started": getFormattedDateStringFromDateObject(startDate),
        "Scenario Finished": getFormattedDateStringFromDateObject(endDate),
        "Scenario Duration": msToFullTime(timeDiff),
        // "CPR Band Data Available": where does this come from,
        Scenario: this.debrief.scenario.name,
        "Scenario ID": this.debrief.scenario.id,
      };
    },
    debriefScores() {
      if (!this.debrief.debriefJson.scores) return {};

      const scores = this.debrief.debriefJson.scores;

      return {
        "Total Positive": scores.numPositiveChecks,
        "Total Negative": scores.numNegativeChecks,
        Total: `${scores.totalCheckScore}/${scores.maxCheckScore}`,
        "Graded Positive": `${scores.gradedScore}/${scores.maxGradedScore}`,
        Cumulative: scores.cumulativeScore,
        Critical: `${scores.criticalScore}/${scores.maxCriticalScore}`,
      };
    },
    numDebriefsForStudent() {
      return this.$store.getters["tmsStore/numScenariosForStudent"](
        this.debrief.student.studentId
      );
    },
    events() {
      const ret = [];

      for (const event of this.debrief.debriefJson.eventList) {
        ret.push(buildDebriefEvent(event));
      }

      const debriefStartDateTimestamp = getDateFromUtcString(
        this.debrief.startDate
      ).getTime();

      const debriefEndDateTimestamp = getDateFromUtcString(
        this.debrief.endDate
      ).getTime();
      const prettyEndTime = debriefEndDateTimestamp - debriefStartDateTimestamp;

      ret.push({
        time: msToFullTime(prettyEndTime, true),
        name: "Scenario Stopped",
        img: require("@/assets/tms-event-icons/" + getImageForDebriefEvent(1)),
      });

      return ret;
    },
  },
  methods: {
    closeDebrief() {
      this.$store.commit("tmsStore/setCurrentDebrief", null);
    },
  },
};
</script>

<style scoped>
.unselected-btn {
  background: var(--isim-primary-light);
  border: 1px solid var(--isim-primary);
  color: var(--isim-primary);
}
.unselected-btn:hover {
  background: var(--isim-light-icon);
}

.user-select-none {
  user-select: none;
}

.debrief-score {
  background: var(--isim-light-icon);
  border-radius: 15px;
  width: 155px;
  height: 75px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.score-name {
  font-size: 0.9em;
  font-weight: 200;
}

.score-num {
  font-size: 1.3em;
}

/*
Applies a filter to recolour to #599ed8 (isim primary blue)
Generated from https://codepen.io/sosuke/pen/Pjoqqp
*/
.image-filter {
  filter: invert(62%) sepia(34%) saturate(703%) hue-rotate(167deg)
    brightness(88%) contrast(91%);
}
</style>
